import { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Input, InputField, Button } from '@ets/design-system';

import { useSelector, useDispatch } from '_common/hooks';

import { setAuthenticated } from 'Auth/redux/authSlice';
import { useActivateDeviceMutation } from '_common/services/api/authority';

import { LoadingIndicatorFull } from '_common/components';
import SecondaryAuthPageContainer from 'Auth/components/SecondaryAuthPageContainer/SecondaryAuthPageContainer';

import styles from './DeviceRegistrationPage.module.scss';

export const TEST_IDS = {
  CODE: {
    INPUT: 'email',
    ERROR: 'codeError',
  },
  DEVICE: {
    INPUT: 'email',
    ERROR: 'deviceError',
  },
  BUTTON: 'registerDevice',
};

type DeviceRegistrationPageValidations = { code?: string; deviceName?: string };

const DeviceRegistrationPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const sendingNewCode = useSelector((state) => state.auth.sendingNewCode);
  const [activateDevice, { isLoading }] = useActivateDeviceMutation();

  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [validations, setValidations] = useState<DeviceRegistrationPageValidations>({
    code: '',
    deviceName: '',
  });

  useEffect(() => {
    if (!isLoading) {
      setSubmitting(false);
    }
  }, [isLoading]);

  const goToSignIn = () => {
    dispatch(setAuthenticated(false));
  };

  const handleOnRegisterDevice = () => {
    const validations: DeviceRegistrationPageValidations = {};
    if (!code) {
      validations.code = intl.formatMessage({
        id: 'auth.errors.deviceCodeRequired',
      });
    }
    if (!deviceName) {
      validations.deviceName = intl.formatMessage({
        id: 'auth.errors.deviceNameRequired',
      });
    }

    if (Object.keys(validations).length > 0) {
      setValidations(validations);
    } else {
      setSubmitting(true);
      activateDevice({ code: Number(code), name: deviceName });
    }
  };

  return (
    <SecondaryAuthPageContainer
      title="NEW_DEVICE_DETECTED"
      description="NEW_DEVICE_DETECTED_DESCRIPTION"
    >
      {sendingNewCode && <LoadingIndicatorFull />}
      <InputField
        size="l"
        label={intl.formatMessage({ id: 'LOGIN_CODE' })}
        feedbackContent={validations.code}
        style={{ textAlign: 'left' }}
        testId={TEST_IDS.CODE.ERROR}
      >
        <Input
          size="l"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={intl.formatMessage({ id: 'LOGIN_CODE_PLACEHOLDER' })}
          hasError={!!validations.code}
          testId="login-code"
        />
      </InputField>
      <InputField
        size="l"
        label={intl.formatMessage({ id: 'DEVICE_NAME' })}
        feedbackContent={validations.deviceName}
        style={{ textAlign: 'left' }}
        testId={TEST_IDS.DEVICE.ERROR}
      >
        <Input
          size="l"
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          placeholder={intl.formatMessage({ id: 'DEVICE_NAME_PLACEHOLDER' })}
          hasError={!!validations.deviceName}
          testId="device-name"
        />
      </InputField>
      <div className={styles.submit}>
        <Button
          size="m"
          variant="primary"
          fullWidth
          onClick={handleOnRegisterDevice}
          loading={submitting}
          testId={TEST_IDS.BUTTON}
        >
          <FormattedMessage id="REGISTER_DEVICE" />
        </Button>
      </div>
      <Button
        size="m"
        variant="link"
        onClick={goToSignIn}
        disabled={submitting}
        testId="sign-in-to-doDOC"
      >
        <FormattedMessage id="SIGN_IN_TO_DODOC" />
      </Button>
    </SecondaryAuthPageContainer>
  );
};

export default DeviceRegistrationPage;
